/**
 * Agrupa bilhetes do Bingo Semanal em uma linha (pai + filhos Amount=0).
 * Usa recurringParentBetId da API; se ausente, faz fallback por seller+loteria+hora.
 */

function ticketId(t) {
  return String(t?.betId ?? t?.BetId ?? '');
}

function parentIdOf(t) {
  const id = t?.recurringParentBetId ?? t?.RecurringParentBetId ?? null;
  return id ? String(id) : null;
}

function isRecurringSchedule(t) {
  return t?.isRecurringBet === true || t?.IsRecurringBet === true;
}

function amountOf(t) {
  return parseFloat(t?.amount ?? t?.Amount ?? 0) || 0;
}

function awardedOf(t) {
  return parseFloat(t?.totalAwarded ?? t?.TotalAwarded ?? 0) || 0;
}

function sellerIdOf(t) {
  return String(t?.sellerId ?? t?.SellerId ?? '');
}

function persistedMs(t) {
  const raw = t?.dateTimePersisted ?? t?.DateTimePersisted;
  const ms = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(ms) ? ms : 0;
}

function lotteryDetailKey(t) {
  const lots = t?.lotteries ?? t?.Lotteries ?? [];
  if (!Array.isArray(lots) || lots.length === 0) return '';
  return lots
    .map((l) => String(l.lotteryDetailId ?? l.LotteryDetailId ?? ''))
    .filter(Boolean)
    .sort()
    .join('|');
}

/** Filho do pacote Bingo Semanal (não é aposta recorrente diária). */
export function isBingoSemanalPackageChild(ticket) {
  const parentId = parentIdOf(ticket);
  if (!parentId) return false;
  if (isRecurringSchedule(ticket)) return false;
  return amountOf(ticket) === 0;
}

function applyFallbackGrouping(tickets) {
  const zeros = tickets.filter(
    (t) => amountOf(t) === 0 && !isRecurringSchedule(t) && !parentIdOf(t),
  );
  if (zeros.length === 0) return tickets;

  const parents = tickets.filter((t) => amountOf(t) > 0);
  const hidden = new Set();
  const childrenByParent = new Map();

  zeros.forEach((child) => {
    const sid = sellerIdOf(child);
    const lotKey = lotteryDetailKey(child);
    const childMs = persistedMs(child);
    const parent = parents.find((p) => {
      if (sellerIdOf(p) !== sid) return false;
      if (lotKey && lotteryDetailKey(p) && lotteryDetailKey(p) !== lotKey) return false;
      return Math.abs(persistedMs(p) - childMs) <= 120000;
    });
    if (!parent) return;
    const pid = ticketId(parent);
    hidden.add(ticketId(child));
    if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
    childrenByParent.get(pid).push(child);
  });

  if (hidden.size === 0) return tickets;

  return tickets
    .filter((t) => !hidden.has(ticketId(t)))
    .map((t) => {
      const children = childrenByParent.get(ticketId(t));
      if (!children?.length) return t;
      const childAwards = children.reduce((s, c) => s + awardedOf(c), 0);
      const totalAwarded = awardedOf(t) + childAwards;
      return {
        ...t,
        totalAwarded,
        TotalAwarded: totalAwarded,
        packageDrawCount:
          (Number(t.packageDrawCount || t.PackageDrawCount || 1) || 1) + children.length,
        isBingoSemanalPackage: true,
      };
    });
}

/**
 * @param {Array} tickets
 * @returns {Array} lista com uma linha por pacote/bilhete
 */
export function groupBingoSemanalTickets(tickets) {
  if (!Array.isArray(tickets) || tickets.length === 0) {
    return [];
  }

  const byId = new Map();
  tickets.forEach((t) => {
    const id = ticketId(t);
    if (id) byId.set(id, t);
  });

  const childrenByParent = new Map();
  tickets.forEach((t) => {
    if (!isBingoSemanalPackageChild(t)) return;
    const pid = parentIdOf(t);
    if (!pid || !byId.has(pid)) return;
    if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
    childrenByParent.get(pid).push(t);
  });

  const hidden = new Set();
  childrenByParent.forEach((children) => {
    children.forEach((c) => hidden.add(ticketId(c)));
  });

  let result = [];
  tickets.forEach((t) => {
    const id = ticketId(t);
    if (hidden.has(id)) return;

    const children = childrenByParent.get(id) || [];
    if (children.length === 0) {
      const count = Number(t.packageDrawCount || t.PackageDrawCount || 1) || 1;
      result.push({
        ...t,
        packageDrawCount: count,
        isBingoSemanalPackage: count > 1,
      });
      return;
    }

    const childAwards = children.reduce((s, c) => s + awardedOf(c), 0);
    const totalAwarded = awardedOf(t) + childAwards;
    result.push({
      ...t,
      totalAwarded,
      TotalAwarded: totalAwarded,
      prizeValue: totalAwarded,
      packageDrawCount: 1 + children.length,
      isBingoSemanalPackage: true,
      packageChildIds: children.map(ticketId),
      qrCode:
        t.qrCode ||
        t.QRCode ||
        children.map((c) => c.qrCode || c.QRCode).find(Boolean) ||
        null,
    });
  });

  // API antiga sem recurringParentBetId: agrupa R$ 0 pela mesma venda
  if (childrenByParent.size === 0) {
    result = applyFallbackGrouping(result);
  }

  return result;
}
